import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Clock } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { LeadForm } from './LeadForm';
const STORAGE_KEY = 'ase-lead-popup-seen';
const DELAY_MS = 25000;
const SCROLL_TRIGGER = 0.45;
export function LeadFormModal() {
  const {
    t
  } = useLang();
  const m = t.leadPopup;
  const [open, setOpen] = useState(false);

  // Show once per session: after a delay or when the visitor scrolls ~45% of the page
  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    const show = () => {
      if (sessionStorage.getItem(STORAGE_KEY)) return;
      sessionStorage.setItem(STORAGE_KEY, '1');
      setOpen(true);
      cleanup();
    };
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max > SCROLL_TRIGGER) show();
    };
    const timer = window.setTimeout(show, DELAY_MS);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
    return cleanup;
  }, []);

  // Escape to close + lock background scroll
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open]);
  return <AnimatePresence data-id="element-259" data-mp="2vzobjnj2mtnl">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/75 backdrop-blur-sm" onClick={() => setOpen(false)} role="dialog" aria-modal="true" aria-labelledby="lead-popup-title" data-id="element-260" data-mp="2vwjxlyft0jjq">
          <motion.div initial={{
        opacity: 0,
        y: 24,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 24,
        scale: 0.96
      }} transition={{
        duration: 0.28,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={e => e.stopPropagation()} className="relative w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-dark-100 border border-gold/30 rounded-t-3xl sm:rounded-3xl shadow-2xl" data-id="element-261" data-mp="2cdcy1a162w7c">
            <button onClick={() => setOpen(false)} aria-label={t.legal.close} className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors" data-id="element-262" data-mp="2hcfui632zu3p">
              <X className="w-5 h-5" data-id="element-263" data-mp="3erlcjz76236k" />
            </button>

            <div className="px-6 pt-8 pb-6 sm:px-8 border-b border-white/10" data-id="element-264" data-mp="3et4wbzs16wd3">
              <div className="inline-flex items-center gap-2 text-gold text-sm font-bold mb-3" data-id="element-265" data-mp="3o5c2n3b21yob">
                <Clock className="w-4 h-4" data-id="element-266" data-mp="3k3qtnt1n64je" />
                {m.badge}
              </div>
              <h2 id="lead-popup-title" className="text-2xl sm:text-3xl font-bold text-white leading-tight pr-8" data-id="element-267" data-mp="1r2e8u74kqnj">
                {m.titleA} <span className="text-gold" data-id="element-268" data-mp="3579h4yhpujv9">{m.titleHighlight}</span>
              </h2>
              <p className="mt-3 text-gray-400" data-id="element-269" data-mp="359ce62hadd9h">{m.subtitle}</p>
            </div>

            <div className="px-6 py-6 sm:px-8" data-id="element-270" data-mp="3180ojwyews1a">
              <LeadForm idPrefix="popup" compact onDone={() => setOpen(false)} data-id="element-271" data-mp="3f5yzsthfs8ck" />
              <button type="button" onClick={() => setOpen(false)} className="mt-4 w-full text-center text-sm text-gray-500 hover:text-gray-300 transition-colors" data-id="element-272" data-mp="3a5aztib8ch67">
                {m.dismiss}
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}